import type { Message } from "../store/types";

type ContextualUpdateTarget = {
  sendContextualUpdate: (text: string) => void;
};

type VoiceHandoffTransition = {
  stopTextSession: () => Promise<void>;
  endUsageSession: () => Promise<void>;
  startVoiceSession: () => Promise<string | undefined>;
  conversation: ContextualUpdateTarget;
  messages: Message[];
};

function buildVoiceHandoffContext(messages: Message[]): string | undefined {
  const recentMessages = messages.slice(-12);
  if (recentMessages.length === 0) return undefined;

  const lines = recentMessages.map((message) => {
    const speaker = message.author === "user" ? "User" : "Assistant";
    return `${speaker}: ${message.text}`;
  });

  return [
    "The user switched from text chat to voice. Continue naturally and use this recent chat context:",
    ...lines,
  ].join("\n");
}

export function sendVoiceHandoffContext(
  conversation: ContextualUpdateTarget,
  messages: Message[]
): void {
  const handoffContext = buildVoiceHandoffContext(messages);
  if (!handoffContext) return;
  conversation.sendContextualUpdate(handoffContext);
}

export async function switchTextSessionToVoice({
  stopTextSession,
  endUsageSession,
  startVoiceSession,
  conversation,
  messages,
}: VoiceHandoffTransition): Promise<string | undefined> {
  await stopTextSession();
  await endUsageSession();
  const conversationId = await startVoiceSession();
  if (conversationId) sendVoiceHandoffContext(conversation, messages);
  return conversationId;
}
